import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { membersAPI } from '../../services/api';

export default function DeceasedMembers() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    membersAPI.deceasedStats()
      .then(setStats)
      .catch(e => setError(e.response?.data?.error || 'Unable to load statistics.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-3">

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Deceased Members</h1>
          <p className="text-xs text-slate-500 mt-0.5">Overview of deceased members, deaths this year, and age-group breakdowns.</p>
        </div>
        <Link to="/members"
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 self-start sm:self-auto">
          ← Back to Members
        </Link>
      </div>

      {loading && (
        <div className="rounded-2xl border border-slate-200/70 bg-white/90 px-4 py-3 text-sm text-slate-500">
          Loading…
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* Summary stat cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <NumCard
              label="Total Deceased"
              value={stats.total_deceased_members ?? 0}
              color="rose"
              icon="M12 9v4.5m0 3h.008v.008H12V16.5Zm8.25 1.5a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0Z"
            />
            <NumCard
              label="Deaths This Year"
              value={stats.deceased_this_year ?? 0}
              color="amber"
              icon="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
            />
            <NumCard
              label="Tracked by Status"
              value="DECEASED"
              color="blue"
              icon="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
              isText
            />
          </div>

          {/* Age group + notes side by side on wide screens */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_0.6fr]">
            {/* Age breakdown */}
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-[0_4px_16px_rgba(15,23,42,0.05)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 mb-3">Age Group Breakdown</p>
              <div className="grid grid-cols-3 gap-3">
                {[['18–30', '18-30'], ['31–50', '31-50'], ['51–100', '51-100']].map(([label, key]) => (
                  <div key={key} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
                    <p className="mt-1.5 text-2xl font-bold text-slate-800">{stats.age_groups?.[key] ?? 0}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Counts are based on age at date of death.</p>
            </div>

            {/* Usage notes */}
            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400 mb-2">Data Notes</p>
              <p className="text-xs leading-5 text-slate-600">
                This dashboard works best when deceased members have a valid{' '}
                <span className="font-semibold text-slate-800">date_of_death</span> and{' '}
                <span className="font-semibold text-slate-800">date_of_birth</span>{' '}
                recorded on their profile.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NumCard({ label, value, color, icon, isText = false }) {
  const colors = {
    rose: { shell: 'border-rose-200/70 bg-rose-50/60',   icon: 'bg-rose-500 text-white' },
    amber:{ shell: 'border-amber-200/70 bg-amber-50/60', icon: 'bg-amber-500 text-white' },
    blue: { shell: 'border-slate-200/70 bg-white/90',    icon: 'bg-[#1b2857] text-white' },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`rounded-2xl border p-4 shadow-[0_4px_16px_rgba(15,23,42,0.05)] ${c.shell}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${c.icon}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
          <p className={`mt-0.5 font-bold tracking-tight text-slate-900 leading-tight ${isText ? 'text-sm' : 'text-lg'}`}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}
